import { FINDS, FIND_CHOICE } from '@sd/content';
import { t, type Locale } from '@sd/i18n';
import { techMods, type Command, type FindCard, type FindOffer, type GameState } from '@sd/sim';
import { gainsText } from './events';
import { escapeHtml } from './format';
import { TICKS_PER_SECOND } from '../game/protocol';

/**
 * A find to pick (#105, a test of the author's idea): the cards a finished
 * scan offers, what each gives, and the time left before the first is taken.
 * Pure parts are exported for tests.
 */
export interface FindCardView {
  readonly title: string;
  readonly gives: string;
  readonly command: Command;
}

/** What a card would give the player now: the numbers of `grantFind`. */
export function cardGains(
  state: GameState,
  me: string,
  offer: FindOffer,
  card: FindCard,
): Record<string, number | string> {
  const level = offer.level;
  switch (card.find) {
    case 'cache': {
      const loot = FINDS.cache.find((c) => c.good === card.good) ?? FINDS.cache[0]!;
      const player = state.players.find((p) => p.id === me);
      const factor = loot.good === 'exotics' && player ? techMods(player.techs).exoticsYield : 1;
      return { [loot.good]: loot.amount * level * factor };
    }
    case 'derelict':
      return { metals: FINDS.derelict.metals * level, fragments: FINDS.derelict.fragments };
    case 'anomaly':
      return { science: FINDS.anomaly.science * level };
    case 'asteroid':
      return { asteroid: 'new' };
  }
}

export function findCards(state: GameState, me: string, locale: Locale, offer: FindOffer) {
  return offer.cards.map((card, i): FindCardView => ({
    title: t(locale, `find.${card.find}.name`),
    gives: gainsText(locale, cardGains(state, me, offer, card)),
    command: { type: 'pickFind', playerId: me, offerId: offer.id, card: i },
  }));
}

export class FindOfferPanel {
  private readonly el: HTMLElement;
  private shown: number | null = null;

  constructor(
    parent: HTMLElement,
    private readonly locale: Locale,
    private readonly me: string,
    order: (command: Command) => void,
  ) {
    this.el = document.createElement('section');
    this.el.className = 'sd-panel sd-finds';
    this.el.setAttribute('aria-live', 'polite');
    this.el.hidden = true;
    parent.append(this.el);
    this.el.addEventListener('click', (event) => {
      const command = (event.target as HTMLElement).closest<HTMLElement>('[data-command]')?.dataset
        .command;
      if (command) order(JSON.parse(command) as Command);
    });
  }

  update(state: GameState) {
    const offer = state.findOffers.find((o) => o.playerId === this.me);
    if (!offer) {
      this.el.hidden = true;
      this.shown = null;
      return;
    }
    const { locale } = this;
    // Built once per offer, so a click is never lost to a redraw; the timer moves in place.
    if (this.shown !== offer.id) {
      this.shown = offer.id;
      const cards = findCards(state, this.me, locale, offer)
        .map(
          (c) =>
            `<button class="sd-find" type="button" data-command="${escapeHtml(JSON.stringify(c.command))}"><b>${escapeHtml(c.title)}</b><span>${escapeHtml(c.gives)}</span></button>`,
        )
        .join('');
      this.el.innerHTML = `
        <div class="sd-finds__head"><h2>${t(locale, 'finds.title', { level: offer.level })}</h2><span class="sd-label" data-bind="left"></span></div>
        <p class="sd-muted">${t(locale, 'finds.hint')}</p>
        <div class="sd-finds__cards">${cards}</div>
        <div class="sd-finds__timer"><span data-bind="timer"></span></div>`;
    }
    this.el.hidden = false;
    const left = Math.max(0, offer.expiresAt - state.tick);
    const seconds = Math.ceil(left / TICKS_PER_SECOND);
    const label = this.el.querySelector<HTMLElement>('[data-bind="left"]')!;
    const text = t(locale, 'finds.left', { seconds });
    if (label.textContent !== text) label.textContent = text;
    this.el.querySelector<HTMLElement>('[data-bind="timer"]')!.style.width =
      `${(100 * left) / FIND_CHOICE.timeoutTicks}%`;
  }

  dispose() {
    this.el.remove();
  }
}
